import React, { useEffect, useState } from 'react';
import MaterialTable, { MTableFilterRow } from 'material-table';
import ChangesDataService from '../../../services/ChangesService';
import DescriptionIcon from '@material-ui/icons/Description';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx/dist/xlsx.full.min.js';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/bg';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getAppTimezone, toTzDate } from '../../../utils/timezone';

const EventsTable = () => {
    const { t, i18n } = useTranslation();
    const [changes, setChanges] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const [loc] = React.useState(() => {
        const locData = localStorage.getItem('loc');
        return locData ? JSON.parse(locData).id : '';
    });

    useEffect(() => {
        retrieveChanges();
    }, []);

    const retrieveChanges = () => {
        return new Promise((resolve, reject) => {
            setIsLoading(true);
            ChangesDataService.getAll(loc)
                .then(response => {
                    setChanges(response.data);
                    setIsLoading(false);
                    resolve();
                })
                .catch(e => {
                    console.log(e);
                    setIsLoading(false);
                    reject(e);
                });
        });
    };

    // * Add Filters to State Start
    const [selectedRows, setSelectedRows] = useState([]);

    const [components] = useState({
        FilterRow: props => <MTableFilterRow {...props} />,
    });

    const formatChanges = (changes, previous_data, rowData) => {
        let formattedInfo = '';
        let style = {};

        // First, determine if this is a new record, edit, or deletion
        if (changes && changes.action === 'DELETE') {
            formattedInfo = `${t('reports.changesReport.removedEntry')}\n`;
            formattedInfo += `─────────────────────\n`;
            formattedInfo += `${t(
                'reports.changesReport.removedEntryTime'
            )}\n${dayjs(changes.time)
                .tz(getAppTimezone())
                .locale(dateConfig.locale)
                .format(dateConfig.format)}\n`;
            formattedInfo += `─────────────────────\n`;
            formattedInfo += `${t(
                'reports.changesReport.removedEntryLocation'
            )}\n${
                rowData.Location ? rowData.Location.name : changes.location
            }\n`;
            formattedInfo += `─────────────────────\n`;
            style = {
                backgroundColor: '#ffebee',
                color: '#c62828',
                padding: '8px',
                borderRadius: '4px',
                margin: '4px 0',
            };
        } else if (!previous_data) {
            formattedInfo = `${t('reports.changesReport.newEntry')}\n`;
            formattedInfo += `─────────────────────\n`;
            if (changes.time) {
                formattedInfo += `${t(
                    'reports.changesReport.newEntryTime'
                )}\n${dayjs(changes.time)
                    .tz(getAppTimezone())
                    .locale(dateConfig.locale)
                    .format(dateConfig.format)}\n`;
            }
            formattedInfo += `─────────────────────\n`;
            if (changes.event) {
                formattedInfo += `${t('reports.changesReport.newEntryType')}\n${
                    changes.event
                }\n`;
            }
            formattedInfo += `─────────────────────\n`;
            if (changes.location) {
                formattedInfo += `${t(
                    'reports.changesReport.newEntryLocation'
                )}\n${
                    rowData.Location ? rowData.Location.name : changes.location
                }\n`;
            }
            formattedInfo += `─────────────────────\n`;
            style = {
                backgroundColor: '#e8f5e9',
                color: '#2e7d32',
                padding: '8px',
                borderRadius: '4px',
                margin: '4px 0',
            };
        } else {
            formattedInfo = `${t('reports.changesReport.editedEntry')}\n`;
            formattedInfo += `─────────────────────\n`;

            if (changes.time) {
                const oldDate = dayjs(previous_data.time)
                    .tz(getAppTimezone())
                    .locale(dateConfig.locale);
                const newDate = dayjs(changes.time)
                    .tz(getAppTimezone())
                    .locale(dateConfig.locale);

                // Check if only time was changed (same date)
                if (
                    oldDate.format('YYYY-MM-DD') ===
                    newDate.format('YYYY-MM-DD')
                ) {
                    formattedInfo += `${t(
                        'reports.changesReport.changedEntryTime'
                    )}\n${oldDate.format('HH:mm:ss')} → ${newDate.format(
                        'HH:mm:ss'
                    )}\n`;
                }
                // Check if only date was changed (same time)
                else if (
                    oldDate.format('HH:mm:ss') === newDate.format('HH:mm:ss')
                ) {
                    formattedInfo += `${t(
                        'reports.changesReport.changedEntryDate'
                    )}\n${oldDate.format('DD/MM/YYYY')} → ${newDate.format(
                        'DD/MM/YYYY'
                    )}\n`;
                }
                // Both date and time were changed
                else {
                    formattedInfo += `${t(
                        'reports.changesReport.changedEntryDateTime'
                    )}\n${oldDate.format(dateConfig.format)} → ${newDate.format(
                        dateConfig.format
                    )}\n`;
                }
            }
            formattedInfo += `─────────────────────\n`;
            if (changes.event) {
                formattedInfo += `${t(
                    'reports.changesReport.changedEntryEvent'
                )}\n${previous_data.event || 'празно'} ➔ ${changes.event}\n`;
            }
            if (changes.location) {
                formattedInfo += `${t(
                    'reports.changesReport.changedEntryLocation'
                )}\n${
                    rowData.Location ? rowData.Location.name : changes.location
                }\n`;
            }

            formattedInfo += `─────────────────────\n`;

            style = {
                backgroundColor: '#fff3e0',
                color: '#ef6c00',
                padding: '8px',
                borderRadius: '4px',
                margin: '4px 0',
            };
        }

        return { formattedInfo, style };
    };

    // Extend dayjs with plugins
    dayjs.extend(utc);
    dayjs.extend(timezone);

    // Function to get date format and locale based on current language
    const getDateFormatAndLocale = () => {
        const language = i18n.language;
        switch (language) {
            case 'en':
                return {
                    format: 'MM/DD/YYYY HH:mm:ss',
                    locale: 'en',
                    dateFormatExport: date => {
                        return `${date.getMonth() +
                            1}/${date.getDate()}/${date.getFullYear()}, ${date
                            .getHours()
                            .toString()
                            .padStart(2, '0')}:${date
                            .getMinutes()
                            .toString()
                            .padStart(2, '0')}:${date
                            .getSeconds()
                            .toString()
                            .padStart(2, '0')}`;
                    },
                };
            case 'es':
                return {
                    format: 'DD/MM/YYYY HH:mm:ss',
                    locale: 'es',
                    dateFormatExport: date => {
                        return `${date
                            .getDate()
                            .toString()
                            .padStart(2, '0')}/${(date.getMonth() + 1)
                            .toString()
                            .padStart(2, '0')}/${date.getFullYear()}, ${date
                            .getHours()
                            .toString()
                            .padStart(2, '0')}:${date
                            .getMinutes()
                            .toString()
                            .padStart(2, '0')}:${date
                            .getSeconds()
                            .toString()
                            .padStart(2, '0')}`;
                    },
                };
            case 'bg':
            default:
                return {
                    format: 'DD.MM.YYYY г., HH:mm:ss ч.',
                    locale: 'bg',
                    dateFormatExport: date => {
                        return `${date
                            .getDate()
                            .toString()
                            .padStart(2, '0')}.${(date.getMonth() + 1)
                            .toString()
                            .padStart(2, '0')}.${date.getFullYear()} г., ${date
                            .getHours()
                            .toString()
                            .padStart(2, '0')}:${date
                            .getMinutes()
                            .toString()
                            .padStart(2, '0')}:${date
                            .getSeconds()
                            .toString()
                            .padStart(2, '0')} ч.`;
                    },
                };
        }
    };

    // Get the current date format configuration
    const [dateConfig, setDateConfig] = useState(getDateFormatAndLocale());

    // Update date config when language changes
    useEffect(() => {
        setDateConfig(getDateFormatAndLocale());
    }, [i18n.language]);

    const columns = [
        // {
        //     title: 'ID',
        //     field: 'id',
        //     width: 100,
        // },
        {
            title: t('reports.changesReport.administrator'),
            field: 'Administrator.name',
            width: 150,
        },
        {
            title: t('reports.changesReport.changes'),
            render: rowData => {
                const user_id = rowData.changes && rowData.changes.user_id;
                const { formattedInfo, style } = formatChanges(
                    rowData.changes,
                    rowData.previous_data,
                    rowData
                );

                return (
                    <div style={{ whiteSpace: 'pre-line' }}>
                        <strong>
                            {rowData.model_name} #{rowData.record_id}
                        </strong>
                        <div style={style}>
                            {formattedInfo}
                            {user_id && (
                                <>
                                    <span>
                                        {t('reports.changesReport.employee')}{' '}
                                        {rowData.User
                                            ? rowData.User.name
                                            : user_id}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            title: t('reports.changesReport.date'),
            field: 'createdAt',
            defaultSort: 'desc',
            dateSetting: {
                locale: dateConfig.locale,
                format: dateConfig.format,
                hour12: false,
            },
            render: rowData => {
                if (
                    rowData.createdAt &&
                    rowData.createdAt !== 'null' &&
                    rowData.createdAt !== 'empty'
                ) {
                    if (i18n.language === 'bg') {
                        // Custom formatting for Bulgarian dates with Cyrillic characters
                        const date = toTzDate(rowData.createdAt);
                        return `${date
                            .getDate()
                            .toString()
                            .padStart(2, '0')}.${(date.getMonth() + 1)
                            .toString()
                            .padStart(2, '0')}.${date.getFullYear()} г., ${date
                            .getHours()
                            .toString()
                            .padStart(2, '0')}:${date
                            .getMinutes()
                            .toString()
                            .padStart(2, '0')}:${date
                            .getSeconds()
                            .toString()
                            .padStart(2, '0')} ч.`;
                    }
                    return dayjs(rowData.createdAt)
                        .tz(getAppTimezone())
                        .locale(dateConfig.locale)
                        .format(dateConfig.format);
                }
                return '';
            },
        },
    ];

    // ! Export Table
    const tableRef = React.createRef();

    const exportTable = () => {
        const workbook = XLSX.utils.book_new(); // Create a new workbook
        const transformedData = changes.map(rowData => {
            // Replicate the formatting logic for new information
            let formattedNewInfo = '';
            const { event, user_id, location, Administrator } = rowData.changes;
            const timeValue = rowData.changes && rowData.changes.time;

            // Formatting logic for new information
            if (timeValue && timeValue.trim() !== '') {
                const date = new Date(timeValue);
                const tz = getAppTimezone();
                const dateOptions = {
                    day: 'numeric',
                    month: 'numeric',
                    year: 'numeric',
                    timeZone: tz,
                };
                const timeOptions = {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false,
                    timeZone: tz,
                };

                const formattedDate =
                    new Intl.DateTimeFormat('bg-BG', dateOptions).format(date) +
                    ', ' +
                    new Intl.DateTimeFormat('bg-BG', timeOptions).format(date);

                formattedNewInfo += `Дата и час:\r\n ${formattedDate}\r\n`;
                if (event)
                    formattedNewInfo += `─────────────────────\r\nВид събитие: ${event}\r\n`;
                if (user_id)
                    formattedNewInfo += `─────────────────────\r\nСлужител: ${user_id}`;
                // Add other fields like 'location' if needed
            } else {
                formattedNewInfo = 'Това е ново събитие.';
            }

            // Replicate the formatting logic for old information
            let formattedOldInfo = '';
            const previousTimeValue =
                rowData.previous_data && rowData.previous_data.time;
            if (previousTimeValue && previousTimeValue.trim() !== '') {
                const date = new Date(previousTimeValue);
                const tz = getAppTimezone();
                const formattedDate =
                    new Intl.DateTimeFormat('bg-BG', {
                        day: 'numeric',
                        month: 'numeric',
                        year: 'numeric',
                        timeZone: tz,
                    }).format(date) +
                    ', ' +
                    new Intl.DateTimeFormat('bg-BG', {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false,
                        timeZone: tz,
                    }).format(date);

                formattedOldInfo = formattedDate;
            } else {
                formattedOldInfo = 'Това е ново събитие.';
            }

            // Formatting logic for createdAt
            const createdAtDate = new Date(rowData.createdAt);
            const tzCreated = getAppTimezone();
            const formattedCreatedAt =
                new Intl.DateTimeFormat('bg-BG', {
                    day: 'numeric',
                    month: 'numeric',
                    year: 'numeric',
                    timeZone: tzCreated,
                }).format(createdAtDate) +
                ', ' +
                new Intl.DateTimeFormat('bg-BG', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false,
                    timeZone: tzCreated,
                }).format(createdAtDate);

            const manualAdded = rowData.previous_data ? 'Не' : 'Да';
            const corrected = rowData.previous_data ? 'Да' : 'Не';
            const eventType =
                rowData.previous_data && rowData.previous_data.event;

            return {
                Администратор: rowData.Administrator.name,
                'Нова информация': formattedNewInfo,
                'Стара информация': formattedOldInfo, // Updated to use formattedOldInfo
                'Вид Събите': eventType,
                'Ръчно добавено': manualAdded,
                Коригирано: corrected,
                'Дата на създаване на промяна': formattedCreatedAt,
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(transformedData);

        XLSX.utils.book_append_sheet(workbook, worksheet, 'Changes');
        const excelBuffer = XLSX.write(workbook, {
            bookType: 'xlsx',
            type: 'array',
        });
        const data = new Blob([excelBuffer], {
            type:
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        saveAs(data, 'Промени.xlsx');
    };

    return (
        <div>
            <MaterialTable
                title={t('reports.changesReport.title')}
                columns={columns}
                data={changes}
                isLoading={isLoading}
                options={{
                    loadingType: 'linear',
                    pageSize: 50,
                    maxBodyHeight: '68vh',
                    showTitle: true,
                    sorting: true,
                    actionsColumnIndex: -1,
                    filtering: true,
                    pageSizeOptions: [50, 100, 200, 300, 400, 500],
                }}
                localization={{
                    pagination: {
                        labelDisplayedRows:
                            '{from}-{to} ' +
                            t('reports.common.of') +
                            ' {count}',
                        labelRowsPerPage: t('reports.common.rowsPerPage'),
                        labelRowsSelect: t('reports.common.rowsSelect'),
                    },
                    header: {
                        actions: t('reports.common.actions'),
                    },
                    body: {
                        emptyDataSourceMessage: t(
                            'reports.common.emptyDataSourceMessage'
                        ),
                        editTooltip: t('reports.common.edit'),
                        deleteTooltip: t('reports.common.delete'),
                        filterRow: {
                            filterTooltip: t('reports.common.filter'),
                        },
                        editRow: {
                            deleteText: t('reports.common.deleteConfirmation'),
                            cancelTooltip: t('reports.common.cancel'),
                            saveTooltip: t('reports.common.save'),
                        },
                    },
                    toolbar: {
                        exportTitle: t('reports.common.export'),
                        exportName: t('reports.common.exportCSV'),
                        searchTooltip: t('reports.common.search'),
                        searchPlaceholder: t(
                            'reports.common.searchPlaceholder'
                        ),
                        nRowsSelected: t('reports.common.rowsSelected', {
                            count: selectedRows.length,
                        }),
                    },
                }}
            />
        </div>
    );
};

export default EventsTable;
